import React from 'react';
import Checkbox from '@cx-web/components/primitives/Checkbox';
import TextInput from '@cx-web/components/primitives/TextInput';

import Banner from './Banner';
import { isValidEmailFormat } from './email';
import LoadingSpinner from './LoadingSpinner';
import styles from './MyOCLogin2018.module.css';
import { type LoginViewProps } from './types';

/**
 * Login form ported from the legacy widget's `login-view`. Presentational: it owns the ephemeral input
 * state and local email-format / empty-field validation, and delegates the actual auth call to the
 * container via `onSubmit`. Loading + post-attempt banner are container-controlled props.
 *
 * Dropped vs legacy (NGF-6371 scope): social login buttons, the social-explanation popup, and the
 * Google One-Tap render path.
 */
const LoginView: React.FC<LoginViewProps> = ({
  banner,
  defaultEmail = '',
  fromEmailVerification = false,
  labelActions,
  loading = false,
  onForgotPassword,
  onSubmit,
}) => {
  const [email, setEmail] = React.useState(defaultEmail);
  const [password, setPassword] = React.useState('');
  const [rememberMe, setRememberMe] = React.useState(true);
  const [isValidEmail, setIsValidEmail] = React.useState(true);
  const [emptyFields, setEmptyFields] = React.useState(false);

  const hasError = !isValidEmail || emptyFields || banner?.type === 'error';

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!email || !password) {
      setEmptyFields(true);
      return;
    }
    setEmptyFields(false);
    onSubmit({ email, password, rememberMe });
  };

  if (loading) {
    return (
      <form className={styles.view}>
        <LoadingSpinner />
      </form>
    );
  }

  const titleLabel = fromEmailVerification
    ? 'AUTH_LOGIN_TO_ODDSCHECKER_FROM_EMAIL_VERIFICATION'
    : 'AUTH_LOGIN_TO_ODDSCHECKER';

  return (
    <form className={styles.view} onSubmit={handleSubmit}>
      <div className={styles.viewContent}>
        <div className={styles.viewTitle}>{labelActions.get(titleLabel)}</div>

        <TextInput
          hasError={hasError}
          name="email"
          onBlur={() => setIsValidEmail(isValidEmailFormat(email))}
          onChange={event => {
            setEmail(event.target.value);
            setEmptyFields(false);
          }}
          placeholder={labelActions.get('AUTH_INPUT_EMAIL')}
          type="email"
          value={email}
          wrapperClassName={styles.field}
        />
        {!isValidEmail && <div className={styles.errorMessage}>{labelActions.get('AUTH_EMAIL_ADDRESS_FORMAT')}</div>}

        <TextInput
          hasError={hasError}
          name="password"
          onChange={event => {
            setPassword(event.target.value);
            setEmptyFields(false);
          }}
          placeholder={labelActions.get('AUTH_INPUT_PASSWORD')}
          type="password"
          value={password}
          wrapperClassName={styles.field}
        />

        {banner && <Banner banner={banner} resendLabel={labelActions.get('AUTH_RESEND_EMAIL')} />}

        <div className={styles.buttonWrapper}>
          <button className={styles.ctaButton} type="submit">
            {labelActions.get('AUTH_LOGIN')}
          </button>
          <div className={styles.buttonLinks}>
            <Checkbox
              checked={rememberMe}
              onClick={() => setRememberMe(value => !value)}
              testId="myoc-auth-remember-me"
            >
              <span>{labelActions.get('AUTH_KEEP_ME_LOGGED_IN')}</span>
            </Checkbox>
            <button className={styles.forgottenLink} onClick={onForgotPassword} type="button">
              {labelActions.get('AUTH_FORGOT_PASSWORD')}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default LoginView;
